// The inspector's own status: available, busy or away.
//
// Held here, written to /presence, and read by whatever else wants to know who
// can take a call. Two rules shape all of it:
//
//   The status on screen is what the person chose. It is set optimistically and
//   never rolled back by a failed write - being told "you are not actually
//   away" by a network error is worse than a row that is briefly behind.
//
//   The row must not rot. A status nobody refreshes is indistinguishable from
//   one nobody meant, so this heartbeats while the app is open and the endpoint
//   records when it last heard from us.

import { useCallback, useEffect, useRef, useState } from 'react';
import { endpoints } from '../config/endpoints';
import { getToken } from '../auth/session';

export type Status = 'available' | 'busy' | 'away';
export type StatusSource = 'manual' | 'call';

export const STATUS_LABELS: Record<Status, string> = {
  available: 'Available',
  busy: 'Busy',
  away: 'Away',
};

/**
 * Heartbeat interval. The endpoint's default staleness window is 120s, so this
 * leaves room for three to be missed before anyone is called stale - a laptop
 * that sleeps briefly or a request that times out should not make somebody
 * vanish.
 *
 * Not "every few seconds": a heartbeat re-asserts an unchanged value, and doing
 * it twelve times a minute per signed-in person would spend a great deal to
 * learn nothing. Changes do not wait for it - they are sent immediately.
 */
const HEARTBEAT_MS = 30_000;

/**
 * Changes are sent straight away, but two changes inside this window collapse
 * into one. Somebody clicking through the options to see them is one intention,
 * not three.
 */
const SETTLE_MS = 400;

export type PresenceApi = {
  status: Status;
  /** How it got set. A call sets 'call'; the picker sets 'manual'. */
  source: StatusSource;
  /** Last write failed. Shown quietly - the status on screen still stands. */
  problem: string | null;
  setStatus: (status: Status, source?: StatusSource) => void;
  /** A call started or ended. See setOnCall for what it does and does not touch. */
  setOnCall: (active: boolean) => void;
};

async function send(body: Record<string, unknown>): Promise<string | null> {
  if (!endpoints.presence) return 'This build has no presence endpoint.';
  const token = getToken();
  if (!token) return 'Signed out, so your status was not saved.';

  try {
    const response = await fetch(`${endpoints.presence.replace(/\/+$/, '')}/`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json', 'x-api-key': token },
      body: JSON.stringify(body),
      // Lets a status change survive the tab closing, which is exactly when
      // "busy" most needs to stop being true.
      keepalive: true,
    });
    if (!response.ok) return `Your status was not saved (${response.status}).`;
    return null;
  } catch {
    return 'Could not reach the presence service.';
  }
}

export function usePresence(): PresenceApi {
  const [status, setStatusState] = useState<Status>('available');
  const [source, setSource] = useState<StatusSource>('manual');
  const [problem, setProblem] = useState<string | null>(null);

  // What a write should say, read by the timer without re-arming it on every
  // change.
  const latest = useRef({ status, source });
  latest.current = { status, source };

  const settle = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** What they were before the call, so leaving it can put them back. */
  const beforeCall = useRef<{ status: Status; source: StatusSource } | null>(null);

  const setStatus = useCallback((next: Status, nextSource: StatusSource = 'manual') => {
    // Optimistic, and deliberately not reverted if the write fails.
    setStatusState(next);
    setSource(nextSource);

    if (settle.current) clearTimeout(settle.current);
    settle.current = setTimeout(() => {
      void send({ status: next, source: nextSource }).then(setProblem);
    }, SETTLE_MS);
  }, []);

  /**
   * Being on a call is the clearest kind of busy there is, and nobody should
   * have to remember to say so.
   *
   * Leaving restores whatever they were before, rather than assuming
   * "available" - somebody who was away before the call is still away after it.
   * And if they picked a status *during* the call, that is theirs: the restore
   * only undoes a busy this put there in the first place.
   */
  const setOnCall = useCallback(
    (active: boolean) => {
      if (active) {
        beforeCall.current = latest.current;
        setStatus('busy', 'call');
        return;
      }

      const prior = beforeCall.current;
      beforeCall.current = null;

      const stillOurs = latest.current.status === 'busy' && latest.current.source === 'call';
      if (!stillOurs) return;

      setStatus(prior?.status ?? 'available', prior?.source ?? 'manual');
    },
    [setStatus],
  );

  useEffect(() => {
    // One write on open, so somebody who never touches the control still
    // appears - signed in and quiet is a kind of presence too.
    void send({ status: latest.current.status, source: latest.current.source }).then(setProblem);

    const timer = setInterval(() => {
      // No status: a heartbeat, which touches only when we were last seen and
      // leaves "busy since" alone.
      void send({}).then(setProblem);
    }, HEARTBEAT_MS);

    return () => clearInterval(timer);
  }, []);

  return { status, source, problem, setStatus, setOnCall };
}
